import { useState, useEffect, useCallback } from 'react';
import { useSessionState } from '../../hooks/useSessionState';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Edit, Trash2, Copy, ChevronLeft, ChevronRight,
  FileText, FilterX,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { api } from '../../core/apiClient';
import { ExcelFilterDropdown } from '../../components/ui/excel-filter';

interface Client { id: string; name: string; }
interface Brand  { id: string; name: string; code: string; primaryColor?: string; }

interface Budget {
  id: string;
  budgetNumber: string;
  budgetDate: string;
  validityDate?: string;
  estat: string;
  projecte?: string;
  total: number;
  baseImposable: number;
  acceptedAt?: string | null;
  etiquetaExpedient?: string | null;
  client: Client;
  brand?: Brand | null;
  projectId?: string | null;
  ecId?: string | null;
}

interface PaginatedResponse {
  success: boolean;
  data: Budget[];
  pagination: { total: number; page: number; limit: number; totalPages: number };
}

// Nomes colors per codi (el nom/etiqueta ve sempre del catàleg unificat
// admin/estats-crm — vegeu estatLabel més avall; aquest diccionari no s'ha
// de fer servir mai per al text, nomes per l'estil del badge).
const ESTAT_COLOR: Record<string, string> = {
  ESBORRANY:  'bg-gray-100 text-gray-700',
  ENVIAT:     'bg-blue-100 text-blue-700',
  DESESTIMAT: 'bg-slate-100 text-slate-700',
  ADJUDICAT:  'bg-green-100 text-green-700',
  FACTIBLE:   'bg-amber-100 text-amber-700',
  ACCEPTAT:   'bg-emerald-100 text-emerald-700',
  REBUTJAT:   'bg-gray-100 text-gray-600',
  LLIURAT:    'bg-purple-100 text-purple-700',
  FACTURAT:   'bg-teal-100 text-teal-700',
};

interface EstatOpt { code: string; name: string; }

const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString('ca-ES') : '—');
const fmtCurrency = (v: number) =>
  Number(v || 0).toLocaleString('ca-ES', { style: 'currency', currency: 'EUR' });

export default function BudgetList() {
  const navigate = useNavigate();

  const [budgets, setBudgets]     = useState<Budget[]>([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]           = useSessionState('budgets:search', '');
  const [estatFilter, setEstatFilter] = useSessionState('budgets:estatFilter', '');
  const [brandFilter, setBrandFilter] = useSessionState('budgets:brandFilter', '');
  const [brands, setBrands]       = useState<Brand[]>([]);
  const [estatsOpts, setEstatsOpts] = useState<EstatOpt[]>([]);
  const [page, setPage]           = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]         = useState(0);
  const [deleting, setDeleting]   = useState<string | null>(null);
  const [duplicating, setDuplicating] = useState<string | null>(null);
  const [duplicateModal, setDuplicateModal] = useState<Budget | null>(null);
  const [duplicateEcOption, setDuplicateEcOption] = useState<'same' | 'other'>('same');
  const [duplicateEcId, setDuplicateEcId] = useState('');
  const [duplicateEcSelected, setDuplicateEcSelected] = useState<{ id: string; ecNumber: string; name: string; etiquetaExpedient?: string | null; client?: { name: string } } | null>(null);
  const [duplicateEcSearch, setDuplicateEcSearch] = useState('');
  const [duplicateEcResults, setDuplicateEcResults] = useState<{ id: string; ecNumber: string; name: string; etiquetaExpedient?: string | null; client?: { name: string } }[]>([]);
  const [searchingDuplicateEc, setSearchingDuplicateEc] = useState(false);
  const [sortBy, setSortBy]   = useSessionState<string>('budgets:sortBy', 'budgetDate');
  const [sortDir, setSortDir] = useSessionState<'ASC' | 'DESC'>('budgets:sortDir', 'DESC');
  const [editingAcceptedAtId, setEditingAcceptedAtId] = useState<string | null>(null);

  // Nom sempre des del catàleg unificat (editable a Admin > Taules > CRM),
  // mateix patró que ExpedientComercialList.tsx — ESTAT_COLOR nomes dona
  // l'estil si el codi és conegut.
  const estatLabel = (code: string) => estatsOpts.find((e) => e.code === code)?.name ?? code;

  const toggleSort = (col: string) => {
    if (sortBy === col) setSortDir((d) => (d === 'ASC' ? 'DESC' : 'ASC'));
    else { setSortBy(col); setSortDir('ASC'); }
    setPage(1);
  };
  const sortIcon = (col: string) => sortBy === col
    ? <span className="ml-1 text-foreground">{sortDir === 'ASC' ? '↑' : '↓'}</span>
    : <span className="ml-1 text-muted-foreground/40">↕</span>;

  useEffect(() => {
    api.get<{ success: boolean; data: Brand[] }>('/brands').then((r) => setBrands(r.data ?? [])).catch(() => {});
    api.get<{ success: boolean; data: (EstatOpt & { isActive: number })[] }>('/admin/estats-crm', { module: 'pressupost' })
      .then((r) => setEstatsOpts((r.data ?? []).filter((e) => e.isActive)))
      .catch(() => setEstatsOpts([]));
  }, []);

  const fetchBudgets = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page, limit: 20, sortBy, sortDir };
      if (search)      params.search  = search;
      if (estatFilter) params.estat   = estatFilter;
      if (brandFilter) params.brandId = brandFilter;

      const res = await api.get<PaginatedResponse>('/budgets', params);
      setBudgets(res.data ?? []);
      setTotalPages(res.pagination?.totalPages ?? 1);
      setTotal(res.pagination?.total ?? 0);
    } catch (err) {
      console.error('Error carregant pressupostos:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, estatFilter, brandFilter, sortBy, sortDir]);

  useEffect(() => { fetchBudgets(); }, [fetchBudgets]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchBudgets();
  };

  const handleDelete = async (id: string, num: string) => {
    if (!confirm(`Eliminar el pressupost ${num}?`)) return;
    setDeleting(id);
    try {
      await api.delete(`/budgets/${id}`);
      fetchBudgets();
    } catch (err) {
      console.error('Error eliminant:', err);
    } finally {
      setDeleting(null);
    }
  };

  const openDuplicateModal = (b: Budget) => {
    setDuplicateEcOption('same');
    setDuplicateEcId('');
    setDuplicateEcSelected(null);
    setDuplicateEcSearch('');
    setDuplicateEcResults([]);
    setDuplicateModal(b);
  };

  const searchDuplicateEcs = async (query: string) => {
    setDuplicateEcSearch(query);
    if (query.trim().length < 2) { setDuplicateEcResults([]); return; }
    setSearchingDuplicateEc(true);
    try {
      const res = await api.get<{ success: boolean; data: { id: string; ecNumber: string; name: string; etiquetaExpedient?: string | null; client?: { name: string } }[] }>(
        '/expedients-comercials', { search: query, limit: 20 },
      );
      setDuplicateEcResults((res.data ?? []).filter((ec) => ec.id !== duplicateModal?.ecId));
    } catch { setDuplicateEcResults([]); }
    finally { setSearchingDuplicateEc(false); }
  };

  const handleAcceptedAtChange = async (id: string, value: string) => {
    setEditingAcceptedAtId(null);
    try {
      await api.put(`/budgets/${id}`, { acceptedAt: value || null });
      fetchBudgets();
    } catch (err) {
      console.error('Error actualitzant la data d\'acceptació:', err);
    }
  };

  const handleDuplicate = async () => {
    if (!duplicateModal) return;
    const id = duplicateModal.id;
    setDuplicating(id);
    try {
      const ecId = duplicateEcOption === 'same' ? (duplicateModal.ecId || null) : (duplicateEcId || null);
      const res = await api.post<{ success: boolean; data: Budget }>(`/budgets/${id}/duplicate`, { ecId });
      if (res.success) {
        setDuplicateModal(null);
        fetchBudgets();
        navigate(`/crm/pressupostos/${res.data.id}/edit`);
      }
    } catch (err) {
      console.error('Error duplicant:', err);
    } finally {
      setDuplicating(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <Card>
        <CardContent className="pt-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => { setSearch(''); setEstatFilter(''); setBrandFilter(''); setPage(1); }}
              title="Netejar tots els filtres"
              className="flex items-center justify-center px-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shrink-0"
            >
              <FilterX size={16} className="text-muted-foreground" />
            </button>
            <div className="relative flex-1 min-w-[200px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                autoFocus
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cercar per número, client, projecte..."
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {brands.length > 0 && (
              <ExcelFilterDropdown
                label="Marca"
                options={brands.map((b) => ({ value: b.id, label: b.name }))}
                selected={brandFilter ? brandFilter.split(',') : []}
                onApply={(values) => { setBrandFilter(values.join(',')); setPage(1); }}
              />
            )}
            <ExcelFilterDropdown
              label="Estat"
              options={estatsOpts.map((e) => ({ value: e.code, label: e.name }))}
              selected={estatFilter ? estatFilter.split(',') : []}
              onApply={(values) => { setEstatFilter(values.join(',')); setPage(1); }}
            />
            <button
              type="submit"
              className="px-4 py-2 bg-gray-900 text-white rounded-lg text-xs hover:bg-gray-700 transition-colors"
            >
              Cercar
            </button>
          </form>
        </CardContent>
      </Card>

      {/* Taula */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : budgets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <FileText size={40} className="mb-3 opacity-30" />
              <p className="text-xs">No s'han trobat pressupostos</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('budgetDate')}>Data{sortIcon('budgetDate')}</TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('budgetNumber')}>Número{sortIcon('budgetNumber')}</TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('brand')}>Marca{sortIcon('brand')}</TableHead>
                  <TableHead>Etiqueta expedient</TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('client')}>Client{sortIcon('client')}</TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('acceptedAt')}>Data acceptació{sortIcon('acceptedAt')}</TableHead>
                  <TableHead className="text-right cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('baseImposable')}>Import{sortIcon('baseImposable')}</TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('estat')}>Estat{sortIcon('estat')}</TableHead>
                  <TableHead className="w-28"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {budgets.map((b) => {
                  const cfg = { label: estatLabel(b.estat), className: ESTAT_COLOR[b.estat] ?? 'bg-gray-100 text-gray-600' };
                  return (
                    <TableRow
                      key={b.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => navigate(`/crm/pressupostos/${b.id}/edit`)}
                    >
                      <TableCell className="text-xs text-muted-foreground">{fmtDate(b.budgetDate)}</TableCell>
                      <TableCell className="font-medium">{b.budgetNumber}</TableCell>
                      <TableCell>
                        {b.brand && (
                          <span
                            className="inline-block px-2 py-0.5 rounded text-xs font-medium text-white"
                            style={{ backgroundColor: b.brand.primaryColor || '#6b7280' }}
                          >
                            {b.brand.code}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs max-w-[260px] truncate">{b.etiquetaExpedient ?? '—'}</TableCell>
                      <TableCell className="text-xs max-w-[240px] truncate">{b.client?.name ?? '—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground" onClick={(e) => e.stopPropagation()}>
                        {editingAcceptedAtId === b.id ? (
                          <input
                            type="date"
                            autoFocus
                            defaultValue={b.acceptedAt ? b.acceptedAt.slice(0, 10) : ''}
                            onBlur={(e) => handleAcceptedAtChange(b.id, e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Escape') setEditingAcceptedAtId(null); }}
                            className="border border-gray-200 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <span
                            className="cursor-pointer hover:underline"
                            title="Clic per modificar la data d'acceptació"
                            onClick={() => setEditingAcceptedAtId(b.id)}
                          >
                            {fmtDate(b.acceptedAt ?? undefined)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">{fmtCurrency(b.baseImposable)}</TableCell>
                      <TableCell onClick={(e) => { if (b.estat === 'ACCEPTAT' && b.projectId) e.stopPropagation(); }}>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.className} ${b.estat === 'ACCEPTAT' && b.projectId ? 'cursor-pointer hover:opacity-80' : ''}`}
                          onClick={() => { if (b.estat === 'ACCEPTAT' && b.projectId) navigate(`/taller/expedients/${b.projectId}`); }}
                          title={b.estat === 'ACCEPTAT' && b.projectId ? "Obrir l'expedient de taller" : undefined}
                        >
                          {cfg.label}
                        </span>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            title="Editar"
                            onClick={() => navigate(`/crm/pressupostos/${b.id}/edit`)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            title="Duplicar"
                            onClick={() => openDuplicateModal(b)}
                            disabled={duplicating === b.id}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-green-600 transition-colors disabled:opacity-40"
                          >
                            <Copy size={14} />
                          </button>
                          <button
                            title="Eliminar"
                            onClick={() => handleDelete(b.id, b.budgetNumber)}
                            disabled={deleting === b.id}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors disabled:opacity-40"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Paginació */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Pàgina {page} de {totalPages}</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* ── Modal Duplicar ── */}
      {duplicateModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => duplicating === null && setDuplicateModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-[480px]" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-100">
              <h2 className="font-semibold text-foreground">Duplicar pressupost {duplicateModal.budgetNumber}</h2>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-xs text-muted-foreground">
                Es crearà una còpia d'aquest pressupost en estat Esborrany, amb totes les línies. Vols mantenir-lo al mateix Expedient Comercial o assignar-lo a un altre?
              </p>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="radio" name="duplicateEc" checked={duplicateEcOption === 'same'}
                  onChange={() => setDuplicateEcOption('same')} />
                Mantenir el mateix Expedient Comercial
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="radio" name="duplicateEc" checked={duplicateEcOption === 'other'}
                  onChange={() => setDuplicateEcOption('other')} />
                Assignar a un altre Expedient Comercial (de qualsevol client)
              </label>
              {duplicateEcOption === 'other' && (
                <div>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    value={duplicateEcSelected ? `${duplicateEcSelected.ecNumber} — ${duplicateEcSelected.etiquetaExpedient || duplicateEcSelected.name}` : duplicateEcSearch}
                    onChange={(e) => { setDuplicateEcId(''); setDuplicateEcSelected(null); searchDuplicateEcs(e.target.value); }}
                    placeholder="Cercar per número, nom o client..."
                  />
                  {searchingDuplicateEc && <p className="text-[11px] text-muted-foreground mt-1">Cercant...</p>}
                  {!searchingDuplicateEc && duplicateEcSearch.trim().length >= 2 && !duplicateEcSelected && (
                    <div className="mt-1 max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                      {duplicateEcResults.length === 0 ? (
                        <p className="px-3 py-2 text-[11px] text-muted-foreground">Sense resultats</p>
                      ) : duplicateEcResults.map((ec) => (
                        <div key={ec.id}
                          className="px-3 py-1.5 text-xs hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                          onClick={() => { setDuplicateEcId(ec.id); setDuplicateEcSelected(ec); setDuplicateEcSearch(''); }}>
                          <div className="font-medium">{ec.ecNumber} — {ec.etiquetaExpedient || ec.name}</div>
                          {ec.client && <div className="text-muted-foreground">{ec.client.name}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                  {duplicateEcSelected?.client && (
                    <p className="text-[11px] text-amber-600 mt-1">
                      Aquest expedient és del client "{duplicateEcSelected.client.name}" — el pressupost duplicat canviarà de client.
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-100 flex gap-2 justify-end">
              <button onClick={() => setDuplicateModal(null)} disabled={duplicating !== null}
                className="px-3 py-2 text-xs text-muted-foreground hover:bg-gray-100 rounded disabled:opacity-40">
                Cancel·lar
              </button>
              <button
                onClick={handleDuplicate}
                disabled={duplicating !== null || (duplicateEcOption === 'other' && !duplicateEcId)}
                className="flex items-center gap-1.5 px-4 py-2 bg-gray-600 text-white rounded text-sm font-medium hover:bg-gray-700 disabled:opacity-40">
                <Copy size={14} /> {duplicating !== null ? 'Duplicant...' : 'Duplicar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
